import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { OpportunityDetail, CreateOpportunityInput, OpportunityType } from '../types';

interface OpportunityFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opportunity?: OpportunityDetail | null;
  onSave: (data: CreateOpportunityInput) => Promise<void>;
  isSaving?: boolean;
}

const OPPORTUNITY_TYPES: { value: OpportunityType; label: string }[] = [
  { value: 'expansion', label: 'Expansion' },
  { value: 'upgrade', label: 'Upgrade' },
  { value: 'cross-sell', label: 'Cross-sell' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'other', label: 'Other' },
];

export default function OpportunityFormModal({
  open,
  onOpenChange,
  opportunity,
  onSave,
  isSaving = false,
}: OpportunityFormModalProps) {
  const isEditing = !!opportunity;

  const [title, setTitle] = useState('');
  const [type, setType] = useState<OpportunityType>('expansion');
  const [description, setDescription] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [probability, setProbability] = useState('');
  const [targetDate, setTargetDate] = useState('');

  // Reset form when modal opens/closes or opportunity changes
  useEffect(() => {
    if (open) {
      if (opportunity) {
        setTitle(opportunity.title);
        setType(opportunity.type);
        setDescription(opportunity.description || '');
        setEstimatedValue(opportunity.estimated_value?.toString() || '');
        setProbability(opportunity.probability?.toString() || '');
        setTargetDate(opportunity.target_date?.split('T')[0] || '');
      } else {
        // Reset to defaults for new opportunity
        setTitle('');
        setType('expansion');
        setDescription('');
        setEstimatedValue('');
        setProbability('');
        setTargetDate('');
      }
    }
  }, [open, opportunity]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      return;
    }

    const data: CreateOpportunityInput = {
      title: title.trim(),
      type,
      description: description.trim() || undefined,
      estimatedValue: estimatedValue ? parseFloat(estimatedValue) : undefined,
      probability: probability ? parseInt(probability, 10) : undefined,
      targetDate: targetDate || undefined,
    };

    await onSave(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Edit Opportunity' : 'Add Opportunity'}
            </DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the opportunity details below.'
                : 'Enter the details for the new sales opportunity.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Title */}
            <div className="grid gap-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Add 50 sensors to production line"
                required
              />
            </div>

            {/* Type */}
            <div className="grid gap-2">
              <Label htmlFor="type">Type</Label>
              <Select value={type} onValueChange={(v) => setType(v as OpportunityType)}>
                <SelectTrigger id="type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {OPPORTUNITY_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Additional details about this opportunity..."
                rows={3}
              />
            </div>

            {/* Estimated Value & Probability - side by side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="estimatedValue">Estimated Value (CAD)</Label>
                <Input
                  id="estimatedValue"
                  type="number"
                  min="0"
                  step="0.01"
                  value={estimatedValue}
                  onChange={(e) => setEstimatedValue(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="probability">Probability (%)</Label>
                <Input
                  id="probability"
                  type="number"
                  min="0"
                  max="100"
                  value={probability}
                  onChange={(e) => setProbability(e.target.value)}
                  placeholder="0-100"
                />
              </div>
            </div>

            {/* Target Date */}
            <div className="grid gap-2">
              <Label htmlFor="targetDate">Target Close Date</Label>
              <Input
                id="targetDate"
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !title.trim()}>
              {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Opportunity'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
